/**
 * The floor-button label rule, in one place.
 *
 * Every floor button in monitoring writes the same short label — the car-call
 * pad, the hall-call pad, the VIP pad, the Terminal grid, and the Actions floor
 * strip and selector. The buttons themselves are legitimately different (lock
 * imagery, a spinner keyed on the floor being sent, per-floor tooltips, an
 * enablement predicate, radio semantics), but the text on them is one rule, and
 * a floor labelled differently on two screens is a floor an operator cannot
 * match to the one in front of them. So the rule lives here and each button
 * decides only its own chrome.
 */
import { BankFloorInterface } from "../interfaces/Bank.interface";

/**
 * The text a floor button shows: the floor's name cut to its first three
 * characters and uppercased, falling back to the floor's index when the bank
 * never named the floor.
 *
 * A named floor reads the way the car station in front of the operator does —
 * "L1", "M2", "PH" — and three characters is what fits inside the circle.
 *
 * The fallback is `floorNo`, the floor's own number, NOT its array index. Those
 * differ by one on every bank whose floors start at 1: index 10 is floor 11. An
 * operator pressed the button reading "10", the command went out for index 10,
 * and the call appeared on the floor labelled 11 — every layer agreed and the
 * button was the only thing that lied. Nothing else in monitoring addresses a
 * floor by index in front of a human, so nothing else disagreed with the grid.
 *
 * `floorIndex` remains the last resort for a floor with neither a name nor a
 * number, so a button always renders something rather than going blank.
 *
 * The fallback is a number, not a string, and that is deliberate. Floor 0 is a
 * real floor on banks that number from zero, and React renders the number 0 as
 * "0", where anything that collapsed it to an empty or falsy value would
 * silently leave that button blank.
 *
 * Call sites previously spelled the guard two ways, `floor && floor.name &&
 * floor.name !== ""` and `floor.name && floor.name !== ""`. Both reduce to this
 * one: `""` is already falsy, so the `!== ""` arm never fired on its own, and a
 * nullish `floor` threw in both spellings — the leading `floor &&` could not
 * protect the fallback, which reads `floor.floorIndex` regardless.
 */
export default function floorButtonLabel(floor: BankFloorInterface): string | number {
  if (floor.name) {
    return floor.name.substring(0, 3).toUpperCase();
  }

  // Nullish-checked, not truthy-checked: floor number 0 is a real floor and
  // must not fall through to the index.
  return floor.floorNo ?? floor.floorIndex;
}

/**
 * Which of the three rules above produced a floor's label.
 *
 * The label itself cannot say this — "11" is the same three glyphs whether it
 * is the floor's number or its position in an array — and the difference is
 * the one an operator needs, because only one of the two is a floor the
 * building calls 11. So the reason is reported separately and drawn as a
 * warning next to the buttons.
 */
export type FloorLabelSource = "name" | "floor-number" | "floor-index";

/** The rule `floorButtonLabel` applied to this floor. Mirrors it exactly. */
export function floorLabelSource(floor: BankFloorInterface): FloorLabelSource {
  if (floor.name) return "name";
  return floor.floorNo != null ? "floor-number" : "floor-index";
}

/**
 * The warning a floor strip shows, or null when every floor is named and there
 * is nothing to explain.
 *
 * Two distinct degradations, and they are not equally bad:
 *
 *   * `floor-number` — the bank never named the floor, so the button shows the
 *     floor's own number. That still matches the building, and it is what the
 *     wizard writes by default, so it is worth saying once and no more.
 *   * `floor-index` — the floor has neither a name nor a number. The button
 *     falls back to the array position, which is the one label that can
 *     disagree with the grid, the CDO, and the plate beside the door. This is
 *     a data defect, not a style choice.
 *
 * Reported together rather than per button: twelve identical warning icons down
 * a strip is noise the operator learns to look past, which is the opposite of
 * what a warning is for.
 */
export function unnamedFloorWarning(floors: ReadonlyArray<BankFloorInterface>): string | null {
  const unnamed = floors.filter((f) => floorLabelSource(f) !== "name");
  if (unnamed.length === 0) return null;

  const unnumbered = unnamed.filter((f) => floorLabelSource(f) === "floor-index").length;
  if (unnumbered > 0) {
    return (
      `${unnumbered} of ${floors.length} floors have neither a name nor a floor number, ` +
      "so their buttons show the floor's position in the list. That position can differ " +
      "from the floor number the building uses. Name these floors on the bank's setup step."
    );
  }

  return (
    `${unnamed.length} of ${floors.length} floors are unnamed, so their buttons show the ` +
    "floor number instead of a name. Add names on the bank's setup step to match the " +
    "plates beside the doors."
  );
}
